import { useMediaQuery } from "~/hooks/useMediaQuery";
import Modal from "../ui/Modal";
import { useEffect, useState } from "react";
import FlexContainer from "../ui/FlexContainer";
import ProgressBar from "../ui/ProgressBar";
import { useTranslation } from "react-i18next";
import { steps } from "./steps";
import * as S from "./styles";
import Button from "../ui/Button";
import { useAccount } from "~/hooks/useAccount";
import { IOnboardingChoices } from "~/types/account";
import { editProfile } from "~/api/account/requests";
import handleErrors from "~/helpers/handleErrors";
import { SVG } from "../ui/SVG";
import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons";
import { DesktopOnly, MobileOnly } from "../responsive";

interface Props {
  isShowing: boolean;
  onFinish: () => void | Promise<void>;
}

export const FixedElement = ({
  handleContinue,
}: {
  handleContinue: () => void;
}) => {
  const { t } = useTranslation();
  const isAboveDesktop = useMediaQuery("laptop");

  if (isAboveDesktop) return null;

  return (
    // <S.ButtonFixedContainer>
    //   <Button
    //     bgColor="primary"
    //     color="white"
    //     width="100%"
    //     onClick={handleContinue}>
    //     {t("onboarding.continue")}
    //   </Button>
    // </S.ButtonFixedContainer>
    null
  );
};

export const Onboarding = ({ isShowing, onFinish }: Props) => {
  const { t } = useTranslation();
  const isAboveDesktop = useMediaQuery("laptop");
  const savedStepIndex = localStorage.getItem("onboardingStepIndex");
  const [stepIndex, setStepIndex] = useState(() => {
    savedStepIndex ? parseInt(savedStepIndex, 10) : 0;
    if (savedStepIndex) {
      const savedStepIndexInNumber = parseInt(savedStepIndex, 10);
      if (isNaN(savedStepIndexInNumber)) {
        localStorage.removeItem("onboardingStepIndex");
        return 0;
      }

      if (savedStepIndexInNumber >= steps.length) {
        localStorage.removeItem("onboardingStepIndex");
        return 0;
      }

      return savedStepIndexInNumber;
    }
    return 0;
  });
  const { account, refetch } = useAccount();
  const [onboardingChoices, setOnboardingChoices] = useState<
    IOnboardingChoices | undefined
  >(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (account && !onboardingChoices) {
      setOnboardingChoices(account.onboarding_choices);
    }
  }, [account]);

  if (steps.length === 0 || !account) {
    return null;
  }

  const step = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;
  const isFirstStep = stepIndex === 0;

  const handleBack = () => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    }
  };

  const handleContinue = async () => {
    if (!isLastStep) {
      setStepIndex(stepIndex + 1);
      return;
    }

    // Guard against double-clicks while onFinish refetches plans and navigates.
    if (isSubmitting) return;
    setIsSubmitting(true);

    const toastMessages = {
      loading: t("onboarding.loading"),
      success: t("onboarding.success"),
      error: t("onboarding.error"),
    };

    try {
      const { errors } = await handleErrors(
        () => editProfile({ onboarding_choices: onboardingChoices }),
        toastMessages
      );

      if (!errors) {
        refetch();
        localStorage.setItem("onboardingStepIndex", stepIndex.toString());
        await onFinish();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isShowing={isShowing}
      fixedElement={<FixedElement handleContinue={handleContinue} />}
      minWidth={isAboveDesktop ? "43%" : "95%"}
      padding={isAboveDesktop ? "40px" : "20px"}>
      <FlexContainer flexDirection="column" gap="20px">
        <FlexContainer
          flexDirection="column"
          gap="0"
          width="100%"
          alignItems="flex-start">
          <FlexContainer gap={0.8}>
            {isFirstStep ? null : (
              <Button
                padding="0"
                alignSelf="center"
                onClick={handleBack}
                bgColor="transparent">
                <SVG icon={faChevronLeft} color="textSecondary" />
              </Button>
            )}
            <S.StepTitle>{t(step?.titleKey || "")}</S.StepTitle>
          </FlexContainer>
          {isAboveDesktop ? (
            <ProgressBar
              progress={((stepIndex + 1) / steps.length) * 100}
              color="primary"
            />
          ) : null}
        </FlexContainer>

        <step.component
          onboardingChoices={onboardingChoices}
          setOnboardingChoices={setOnboardingChoices}
        />

        <FlexContainer
          justifyContent="space-between"
          flexDirection={isAboveDesktop ? "row" : "column"}
          width="100%">
          <FlexContainer
            flexDirection="row"
            justifyContent="space-between"
            width="100%"
            gap="8px">
            <Button
              bgColor="transparent"
              color="textSecondary"
              width="auto"
              onClick={handleContinue}>
              {t("onboarding.skip")}
            </Button>

            <Button
              bgColor="primary"
              color="white"
              width="auto"
              padding={"12px 24px"}
              onClick={handleContinue}>
              {t("onboarding.continue")}
            </Button>
          </FlexContainer>
        </FlexContainer>
      </FlexContainer>
    </Modal>
  );
};
